import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  getCountFromServer,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { getDb } from './firebase';
import { deleteCampaignImages } from './storageService';
import type { Campaign, AdLevel, AdCreative } from '../types';

const COLLECTION = 'campaigns';

function toCreative(data: unknown): AdCreative | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const d = data as Record<string, unknown>;
  if (!d.imageUrl) return undefined;
  return {
    imageUrl: d.imageUrl as string,
    tapUrl: (d.tapUrl as string) ?? '',
    rotationSeconds: (d.rotationSeconds as number) ?? 5,
  };
}

function toCampaign(snap: QueryDocumentSnapshot<DocumentData>): Campaign {
  const data = snap.data();
  const levels: Partial<Record<AdLevel, AdCreative>> = {};
  const l1 = toCreative(data.levels?.['1'] ?? data.levels?.[1]);
  const l2 = toCreative(data.levels?.['2'] ?? data.levels?.[2]);
  const l3 = toCreative(data.levels?.['3'] ?? data.levels?.[3]);
  if (l1) levels[1] = l1;
  if (l2) levels[2] = l2;
  if (l3) levels[3] = l3;

  return {
    id: snap.id,
    advertiserId: data.advertiserId,
    name: data.name,
    priority: data.priority ?? 99,
    isActive: data.isActive ?? false,
    startDate:
      data.startDate instanceof Timestamp
        ? data.startDate.toDate().toISOString()
        : (data.startDate ?? null),
    endDate:
      data.endDate instanceof Timestamp
        ? data.endDate.toDate().toISOString()
        : (data.endDate ?? null),
    levels,
    createdAt:
      data.createdAt instanceof Timestamp
        ? data.createdAt.toDate().toISOString()
        : (data.createdAt ?? new Date().toISOString()),
    updatedAt:
      data.updatedAt instanceof Timestamp
        ? data.updatedAt.toDate().toISOString()
        : (data.updatedAt ?? new Date().toISOString()),
  };
}

function toFirestoreCreative(creative: AdCreative) {
  return {
    imageUrl: creative.imageUrl,
    tapUrl: creative.tapUrl,
    rotationSeconds: creative.rotationSeconds,
  };
}

function toFirestoreDate(iso: string | null | undefined): Timestamp | null {
  if (!iso) return null;
  return Timestamp.fromDate(new Date(iso));
}

/**
 * Fetch all campaigns across all advertisers, ordered by priority.
 */
export async function getAllCampaigns(): Promise<Campaign[]> {
  const q = query(collection(getDb(), COLLECTION), orderBy('priority', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(toCampaign);
}

/**
 * Fetch all campaigns for a specific advertiser, ordered by priority.
 */
export async function getCampaignsByAdvertiser(advertiserId: string): Promise<Campaign[]> {
  const q = query(
    collection(getDb(), COLLECTION),
    where('advertiserId', '==', advertiserId),
    orderBy('priority', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(toCampaign);
}

export interface CreateCampaignData {
  advertiserId: string;
  name: string;
  priority: number;
  isActive: boolean;
  startDate?: string | null;
  endDate?: string | null;
  levels: Partial<Record<AdLevel, AdCreative>>;
}

/**
 * Create a new campaign. Returns the generated Firestore document ID.
 */
export async function createCampaign(data: CreateCampaignData): Promise<string> {
  const now = Timestamp.now();
  const levels: Record<string, unknown> = {};
  ([1, 2, 3] as AdLevel[]).forEach(l => {
    if (data.levels[l]) levels[String(l)] = toFirestoreCreative(data.levels[l]!);
  });
  const docRef = await addDoc(collection(getDb(), COLLECTION), {
    advertiserId: data.advertiserId,
    name: data.name,
    priority: data.priority,
    isActive: data.isActive,
    startDate: toFirestoreDate(data.startDate),
    endDate: toFirestoreDate(data.endDate),
    levels,
    createdAt: now,
    updatedAt: now,
  });
  return docRef.id;
}

/**
 * Update an existing campaign.
 */
export async function updateCampaign(id: string, data: Partial<CreateCampaignData>): Promise<void> {
  const payload: Record<string, unknown> = { updatedAt: Timestamp.now() };

  if (data.name !== undefined) payload.name = data.name;
  if (data.priority !== undefined) payload.priority = data.priority;
  if (data.isActive !== undefined) payload.isActive = data.isActive;
  if ('startDate' in data) payload.startDate = toFirestoreDate(data.startDate);
  if ('endDate' in data) payload.endDate = toFirestoreDate(data.endDate);
  if (data.levels !== undefined) {
    const levels: Record<string, unknown> = {};
    ([1, 2, 3] as AdLevel[]).forEach(l => {
      if (data.levels![l]) levels[String(l)] = toFirestoreCreative(data.levels![l]!);
    });
    payload.levels = levels;
  }

  await updateDoc(doc(getDb(), COLLECTION, id), payload);
}

/**
 * Toggle the isActive state.
 */
export async function toggleCampaignActive(id: string, currentValue: boolean): Promise<void> {
  await updateDoc(doc(getDb(), COLLECTION, id), {
    isActive: !currentValue,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Delete a campaign and its associated storage images.
 */
export async function deleteCampaign(id: string): Promise<void> {
  await deleteCampaignImages(id);
  await deleteDoc(doc(getDb(), COLLECTION, id));
}

export async function countActiveCampaigns(): Promise<number> {
  const snap = await getCountFromServer(
    query(collection(getDb(), COLLECTION), where('isActive', '==', true))
  );
  return snap.data().count;
}
